import React, { useEffect, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

// -----------------------------------------------------------------------
// Email Preview (2026-08-27)
//
// Renders each of the 4 founding-list touches with a sample name and
// product so Roger can hand-eyeball the copy before subscribers see it.
// The HTML body is injected into an isolated iframe (srcDoc) so
// preview styles cannot leak into the Brand OS shell.
// -----------------------------------------------------------------------

const PRODUCT_OPTIONS = [
  { key: "MDD",         name: "MDD" },
  { key: "Evident",     name: "Evident" },
  { key: "Mirror",      name: "Mirror" },
  { key: "Kindred",     name: "Kindred" },
  { key: "TheWell",     name: "The Well" },
  { key: "MemIQ",       name: "MemIQ" },
  { key: "Lighthouse",  name: "Lighthouse" },
  { key: "LifeOS",      name: "LifeOS" },
  { key: "Booknook",    name: "BookNookExchange" },
  { key: "Hub",         name: "Hub (all)" },
];

const TOUCHES = [1, 2, 3, 4];

export default function EmailPreview() {
  const [touch, setTouch] = useState(1);
  const [name, setName] = useState("Alex");
  const [product, setProduct] = useState("MDD");
  const [nameDraft, setNameDraft] = useState("Alex");
  const [productDraft, setProductDraft] = useState("MDD");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ name, product });
      const r = await api.get(`/waitlist/email-preview/${touch}?${params}`);
      setPreview(r.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touch, name, product]);

  const applyDrafts = () => {
    setName(nameDraft.trim() || "Alex");
    setProduct(productDraft || "MDD");
  };

  return (
    <Shell>
      <div className="bo-page" data-testid="email-preview">
        <div style={{ marginBottom: 16 }}>
          <div className="bo-eyebrow">Marketing · Waitlist</div>
          <h1
            style={{
              fontFamily: "var(--bo-serif)",
              fontSize: 28,
              margin: "4px 0 4px 0",
            }}
          >
            Email preview
          </h1>
          <p
            style={{
              color: "var(--bo-muted)",
              margin: 0,
              fontSize: 14,
            }}
          >
            Preview each of the 4 founding touches with a sample name +
            product. What you see here is byte-for-byte what a subscriber
            receives, minus the working unsubscribe link.
          </p>
        </div>

        {/* Touch tabs */}
        <div
          className="bo-card"
          style={{ padding: 12, marginBottom: 12 }}
          data-testid="email-preview-controls"
        >
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {TOUCHES.map((t) => {
              const active = touch === t;
              return (
                <button
                  key={t}
                  className={`bo-btn ${active ? "bo-btn--primary" : "bo-btn--ghost"}`}
                  onClick={() => setTouch(t)}
                  data-testid={`email-preview-touch-${t}`}
                  style={{ padding: "8px 14px", fontSize: 13 }}
                >
                  Touch {t}
                </button>
              );
            })}
            <span style={{ flex: 1 }} />
            <input
              className="bo-input"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={applyDrafts}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyDrafts();
                }
              }}
              placeholder="Sample first name"
              style={{ padding: "8px 12px", width: 180 }}
              data-testid="email-preview-name"
            />
            <select
              className="bo-input"
              value={productDraft}
              onChange={(e) => {
                setProductDraft(e.target.value);
                setProduct(e.target.value);
              }}
              style={{ padding: "8px 12px", width: 200 }}
              data-testid="email-preview-product"
            >
              {PRODUCT_OPTIONS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Meta strip */}
        {preview && !loading && (
          <div
            className="bo-card"
            style={{ padding: 16, marginBottom: 12 }}
            data-testid="email-preview-meta"
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 8,
              }}
            >
              <Pill tone="success">{preview.label}</Pill>
              <span
                style={{ fontSize: 13, color: "var(--bo-muted)" }}
                data-testid="email-preview-when"
              >
                {preview.when}
              </span>
            </div>
            <div
              style={{
                fontFamily: "var(--bo-serif)",
                fontSize: 20,
                marginTop: 4,
              }}
              data-testid="email-preview-subject"
            >
              <span
                style={{
                  fontSize: 11,
                  letterSpacing: "0.12em",
                  color: "var(--bo-muted)",
                  textTransform: "uppercase",
                  marginRight: 8,
                }}
              >
                Subject:
              </span>
              {preview.subject}
            </div>
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: "var(--bo-muted)",
              }}
            >
              Sample name: <strong>{preview.sample_name}</strong> · Product:{" "}
              <strong>{preview.product_display}</strong>
            </div>
          </div>
        )}

        {error && <ErrorBanner error={error} testId="email-preview-error" />}
        {loading && <LoadingCard />}
        {!loading && !preview && !error && (
          <EmptyState
            title="No preview loaded"
            description="Pick a touch above to render a sample."
            testId="email-preview-empty"
          />
        )}

        {/* Iframe preview */}
        {preview && !loading && (
          <div
            className="bo-card"
            style={{ padding: 0, overflow: "hidden" }}
            data-testid="email-preview-frame-wrap"
          >
            <iframe
              srcDoc={`<!doctype html><html><body style="margin:0">${preview.html}</body></html>`}
              title={`Touch ${touch} preview`}
              sandbox=""
              style={{
                width: "100%",
                height: "720px",
                border: "none",
                background: "#f8f6f4",
                display: "block",
              }}
              data-testid="email-preview-iframe"
            />
          </div>
        )}

        {/* Editing note */}
        <div
          className="bo-card"
          style={{
            padding: 14,
            marginTop: 12,
            background: "var(--bo-panel-alt, #f6f4ef)",
            fontSize: 13,
            color: "var(--bo-muted)",
            lineHeight: 1.6,
          }}
          data-testid="email-preview-editing-note"
        >
          <strong style={{ color: "var(--bo-ink)" }}>To edit copy:</strong>{" "}
          the touch bodies live in <code>backend/server.py</code>. Touch 1 is
          in <code>_welcome_email_html(...)</code>. Touches 2/3/4 are in{" "}
          <code>_founding_touch_html(touch, product_key, name, unsub_token)</code>.
          Both return <code>(subject, html)</code> tuples. Edit, save,
          restart the backend (<code>supervisorctl restart backend</code>),
          and refresh this page to see the change.
        </div>
      </div>
    </Shell>
  );
}
